import React, { useEffect, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from "../lib/methodology";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Mail, FileSpreadsheet } from "lucide-react";
import { Link } from "react-router-dom";

const ROLES = ["company_manager", "happiness_manager", "project_manager", "collaborator"];

export default function CompanyAdminPage() {
    const { user, refresh } = useAuth();
    const canEditCompany = user?.role === "company_manager" || user?.role === "super_admin";
    const [company, setCompany] = useState(null);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({
        name: "", industry: "", website: "", description: "", idle_timeout_minutes: 30,
        billing_name: "", vat_number: "", billing_address: "", billing_zip: "", billing_city: "", billing_country: "Portugal", billing_email: "",
        smtp_host: "", smtp_port: 465, smtp_user: "", smtp_password: "", smtp_from_email: "", smtp_from_name: "", smtp_use_ssl: true,
    });
    const [showInvite, setShowInvite] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "collaborator", send_invite: false });
    const [smtpTesting, setSmtpTesting] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkUploading, setBulkUploading] = useState(false);

    const load = async () => {
        try {
            const [c, u] = await Promise.all([api.get("/companies/me"), api.get("/users")]);
            setCompany(c.data);
            setUsers(u.data);
            setForm({
                name: c.data.name || "",
                industry: c.data.industry || "",
                website: c.data.website || "",
                description: c.data.description || "",
                idle_timeout_minutes: c.data.idle_timeout_minutes || 30,
                billing_name: c.data.billing_name || "",
                vat_number: c.data.vat_number || "",
                billing_address: c.data.billing_address || "",
                billing_zip: c.data.billing_zip || "",
                billing_city: c.data.billing_city || "",
                billing_country: c.data.billing_country || "Portugal",
                billing_email: c.data.billing_email || "",
                smtp_host: c.data.smtp?.host || "",
                smtp_port: c.data.smtp?.port || 465,
                smtp_user: c.data.smtp?.user || "",
                smtp_password: c.data.smtp?.password || "",
                smtp_from_email: c.data.smtp?.from_email || "",
                smtp_from_name: c.data.smtp?.from_name || "",
                smtp_use_ssl: c.data.smtp?.use_ssl !== false,
            });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => { load(); }, []);

    const saveCompany = async (e) => {
        e.preventDefault();
        try {
            const { smtp_host, smtp_port, smtp_user, smtp_password, smtp_from_email, smtp_from_name, smtp_use_ssl, ...rest } = form;
            const payload = {
                ...rest,
                smtp: {
                    host: smtp_host || "",
                    port: Number(smtp_port) || 465,
                    user: smtp_user || "",
                    password: smtp_password || "",
                    from_email: smtp_from_email || "",
                    from_name: smtp_from_name || "",
                    use_ssl: !!smtp_use_ssl,
                },
            };
            await api.patch("/companies/me", payload);
            toast.success("Dados atualizados");
            await refresh();
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const uploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post("/companies/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Logotipo atualizado");
            await refresh();
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const createUser = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/users", newUser);
            if (newUser.send_invite) {
                if (res.data?.invite_email_sent) {
                    toast.success("Utilizador criado e convite enviado por email");
                } else {
                    toast.success("Utilizador criado (SMTP da empresa não configurado — email não enviado)");
                }
            } else {
                toast.success("Utilizador criado");
            }
            setShowInvite(false);
            setNewUser({ name: "", email: "", password: "", role: "collaborator", send_invite: false });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const updateRole = async (userId, role) => {
        try {
            await api.patch(`/users/${userId}`, { role });
            toast.success("Papel atualizado");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const deleteUser = async (userId) => {
        if (!window.confirm("Eliminar utilizador?")) return;
        try {
            await api.delete(`/users/${userId}`);
            toast.success("Utilizador eliminado");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const testSmtp = async () => {
        setSmtpTesting(true);
        try {
            const res = await api.post("/companies/me/smtp/test");
            if (res.data?.ok) {
                toast.success(res.data.message || `Email de teste enviado para ${res.data.recipient}`);
            } else {
                toast.error(res.data?.message || "Falha ao testar SMTP");
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSmtpTesting(false); }
    };

    const onBulkFile = (e) => {
        const f = e.target.files?.[0];
        setBulkFile(f || null);
        setBulkResult(null);
    };

    const submitBulkImport = async (e) => {
        e.preventDefault();
        if (!bulkFile) return;
        setBulkUploading(true);
        setBulkResult(null);
        try {
            const fd = new FormData();
            fd.append("file", bulkFile);
            const res = await api.post("/users/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBulkResult(res.data);
            if (res.data?.created > 0) {
                toast.success(`${res.data.created} utilizador(es) importado(s)`);
            } else {
                toast.info("Nenhum utilizador novo importado");
            }
            if (res.data?.quota_exceeded) {
                toast.error("Limite de utilizadores atingido — algumas linhas foram ignoradas");
            }
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkUploading(false); }
    };

    if (!company) return <div className="label-caps">A carregar...</div>;

    return (
        <div className="space-y-12" data-testid="company-admin-page">
            <div>
                <div className="label-caps">Administração da empresa</div>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">{company.name}</h1>
                <p className="text-[#52525b] mt-3 max-w-xl">
                    {canEditCompany
                        ? "Atualize os dados da organização, carregue o logotipo e gira os utilizadores e papéis."
                        : "Gira os utilizadores e papéis da sua equipa."}
                </p>
            </div>

            {canEditCompany && (
            <>
            {/* Company info + logo */}
            <section className="grid lg:grid-cols-3 gap-6">
                <div className="card-flat lg:col-span-1">
                    <div className="label-caps">Logotipo</div>
                    <div className="mt-6 h-40 border border-dashed border-[#e4e4e7] flex items-center justify-center">
                        {company.logo_path ? (
                            <img src={fileUrl(company.logo_path)} alt={company.name} className="max-h-36 max-w-full object-contain" data-testid="company-logo-preview" />
                        ) : (
                            <div className="text-[#52525b] text-sm">Sem logotipo</div>
                        )}
                    </div>
                    <label className="btn-outline mt-5 inline-flex cursor-pointer w-full justify-center" data-testid="upload-logo-label">
                        <Upload size={16} /> Carregar logotipo
                        <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="upload-logo-input" />
                    </label>
                    <div className="text-xs text-[#52525b] mt-3">PNG ou JPG até 5MB. Aparece no cabeçalho após login.</div>
                </div>

                <form onSubmit={saveCompany} className="card-flat lg:col-span-2 space-y-5" data-testid="company-form">
                    <div className="label-caps">Caracterização</div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="label-caps block mb-2">Nome</label>
                            <input className="input-clean" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" />
                        </div>
                        <div>
                            <label className="label-caps block mb-2">Setor</label>
                            <input className="input-clean" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="company-industry" />
                        </div>
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Website</label>
                        <input className="input-clean" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="company-website" />
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Descrição</label>
                        <textarea className="input-clean" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="company-description" />
                    </div>

                    <div className="pt-6 border-t border-[#e4e4e7]">
                        <div className="label-caps">Dados de faturação</div>
                        <p className="text-xs text-[#52525b] mt-2 mb-5">
                            Necessários para emitir faturas-recibo no Moloni quando contrata uma subscrição.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="label-caps block mb-2">Razão social</label>
                                <input className="input-clean" value={form.billing_name} onChange={(e) => setForm({ ...form, billing_name: e.target.value })} data-testid="billing-name" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">NIF / VAT</label>
                                <input className="input-clean" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="billing-vat" placeholder="999999990" />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="label-caps block mb-2">Morada</label>
                            <input className="input-clean" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="billing-address" />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-5 mt-5">
                            <div>
                                <label className="label-caps block mb-2">Código postal</label>
                                <input className="input-clean" value={form.billing_zip} onChange={(e) => setForm({ ...form, billing_zip: e.target.value })} data-testid="billing-zip" placeholder="1000-001" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Cidade</label>
                                <input className="input-clean" value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} data-testid="billing-city" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">País</label>
                                <input className="input-clean" value={form.billing_country} onChange={(e) => setForm({ ...form, billing_country: e.target.value })} data-testid="billing-country" />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="label-caps block mb-2">Email para faturação</label>
                            <input className="input-clean" type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} data-testid="billing-email" />
                        </div>
                    </div>

                    {/* SMTP CONFIG */}
                    <div className="pt-6 border-t border-[#e4e4e7]" data-testid="smtp-section">
                        <div className="label-caps">Servidor de email da empresa (SMTP)</div>
                        <p className="text-sm text-[#52525b] mt-1.5 max-w-2xl">
                            Os emails de convite a colaboradores e notificações de fases ativadas são enviados <strong>a partir desta conta</strong>. Deixe em branco para desativar o envio.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-5 mt-5">
                            <div>
                                <label className="label-caps block mb-2">Servidor SMTP</label>
                                <input className="input-clean" placeholder="smtp.gmail.com" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="smtp-host" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Porta</label>
                                <input className="input-clean" type="number" placeholder="465" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} data-testid="smtp-port" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Utilizador</label>
                                <input className="input-clean" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} data-testid="smtp-user" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Palavra-passe</label>
                                <input className="input-clean" type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} data-testid="smtp-password" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Email remetente (From)</label>
                                <input className="input-clean" type="email" placeholder="noreply@empresa.pt" value={form.smtp_from_email} onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })} data-testid="smtp-from-email" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Nome remetente</label>
                                <input className="input-clean" placeholder="Projeto de Felicidade" value={form.smtp_from_name} onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} data-testid="smtp-from-name" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!form.smtp_use_ssl} onChange={(e) => setForm({ ...form, smtp_use_ssl: e.target.checked })} data-testid="smtp-use-ssl" />
                            <span className="text-[#52525b]">Ligação SSL/TLS (recomendado · porta 465). Desative para usar STARTTLS na porta 587.</span>
                        </label>
                        <div className="mt-4 flex items-center gap-3 flex-wrap">
                            <button
                                type="button"
                                onClick={testSmtp}
                                disabled={smtpTesting || !form.smtp_host || !form.smtp_user || !form.smtp_password}
                                className="btn-outline disabled:opacity-50"
                                data-testid="smtp-test-btn"
                                title="Envia um email de teste para o seu próprio endereço"
                            >
                                <Mail size={16} /> {smtpTesting ? "A testar..." : "Testar ligação SMTP"}
                            </button>
                            <span className="text-xs text-[#52525b]">
                                Envia um pequeno email para <code>{user?.email}</code> usando os dados acima. Guarde primeiro se alterou algo.
                            </span>
                        </div>
                    </div>

                    <button type="submit" className="btn-primary" data-testid="save-company-btn">Guardar alterações</button>
                </form>
            </section>
            </>
            )}

            {/* Users */}
            <section>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="label-caps">Utilizadores e papéis</div>
                        <h2 className="font-display text-3xl text-[#0d1628] mt-2">Equipa</h2>
                        {user?.users_limit && (
                            <div className="text-sm text-[#52525b] mt-2" data-testid="users-limit-info">
                                {users.length} / {user.users_limit} utilizadores no plano atual
                            </div>
                        )}
                    </div>
                    <button
                        className="btn-primary disabled:opacity-50"
                        onClick={() => setShowInvite(!showInvite)}
                        disabled={user?.users_limit && users.length >= user.users_limit}
                        data-testid="invite-user-btn"
                    >
                        <Plus size={16} /> Adicionar utilizador
                    </button>
                </div>
                {user?.users_limit && users.length >= user.users_limit && (
                    <div className="bg-[#b89b6f]/15 border border-[#b89b6f] text-[#0d1628] px-5 py-3 mt-4 text-sm" data-testid="users-limit-banner">
                        Atingiu o limite de {user.users_limit} utilizadores do seu plano. Para adicionar mais, atualize para um plano superior em <a href="/app/subscription" className="link-underline font-medium">Subscrição</a>.
                    </div>
                )}

                {/* Bulk Excel import */}
                <div className="card-flat mt-6 space-y-4" data-testid="bulk-import-section">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="label-caps flex items-center gap-2"><FileSpreadsheet size={14} /> Importação em massa (Excel)</div>
                            <p className="text-sm text-[#52525b] mt-1.5 max-w-2xl">
                                Carregue um ficheiro <code>.xlsx</code> com as colunas <strong>name</strong>, <strong>email</strong> e (opcional) <strong>role</strong>
                                {" "}(<em>collaborator</em>, <em>happiness_manager</em>, <em>project_manager</em>). Emails já existentes são ignorados.
                            </p>
                        </div>
                    </div>
                    <form onSubmit={submitBulkImport} className="flex items-end gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                            <label className="label-caps block mb-2">Ficheiro Excel</label>
                            <input
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={onBulkFile}
                                className="block text-sm"
                                data-testid="bulk-import-file"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={!bulkFile || bulkUploading}
                            className="btn-primary disabled:opacity-50"
                            data-testid="bulk-import-submit"
                        >
                            {bulkUploading ? "A importar..." : "Importar"}
                        </button>
                    </form>
                    {bulkResult && (
                        <div className="text-sm space-y-2 pt-3 border-t border-[#e4e4e7]" data-testid="bulk-import-result">
                            <div className="flex flex-wrap gap-x-6 gap-y-1">
                                <span><strong className="text-[#0d1628]">{bulkResult.created}</strong> <span className="text-[#52525b]">criados</span></span>
                                <span><strong className="text-[#0d1628]">{bulkResult.skipped_existing}</strong> <span className="text-[#52525b]">já existiam</span></span>
                                <span><strong className="text-[#0d1628]">{bulkResult.errors?.length || 0}</strong> <span className="text-[#52525b]">com erros</span></span>
                                {bulkResult.users_limit != null && (
                                    <span className="text-[#52525b]">Utilizadores: {bulkResult.users_count}/{bulkResult.users_limit}</span>
                                )}
                            </div>
                            {bulkResult.quota_exceeded && (
                                <div className="bg-[#b89b6f]/15 border border-[#b89b6f] text-[#0d1628] px-4 py-3" data-testid="bulk-import-quota-banner">
                                    Atingiu o limite do plano durante a importação. <Link to="/app/subscription" className="link-underline font-medium">Atualize o plano</Link> para importar os restantes utilizadores.
                                </div>
                            )}
                            {bulkResult.errors?.length > 0 && (
                                <details className="bg-[#fafaf9] border border-[#e4e4e7] px-3 py-2">
                                    <summary className="cursor-pointer text-xs text-[#52525b]">Ver detalhes dos erros</summary>
                                    <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                                        {bulkResult.errors.map((er, i) => (
                                            <li key={`err-${i}-${er.row}`}>
                                                <span className="text-[#b89b6f]">Linha {er.row}</span>
                                                {er.email && <span className="text-[#52525b]"> · {er.email}</span>}
                                                <span className="text-[#0d1628]"> — {er.error}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>

                {showInvite && (
                    <form onSubmit={createUser} className="card-flat mt-6 space-y-5" data-testid="invite-user-form">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="label-caps block mb-2">Nome</label>
                                <input required className="input-clean" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Email</label>
                                <input required type="email" className="input-clean" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Palavra-passe temporária</label>
                                <input required minLength={8} className="input-clean" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Papel</label>
                                <select className="input-clean bg-white" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="new-user-role">
                                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                </select>
                                <p className="text-xs text-[#52525b] mt-1.5">{ROLE_DESCRIPTIONS[newUser.role] || ""}</p>
                            </div>
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer text-sm pt-2 border-t border-[#e4e4e7]" data-testid="new-user-send-invite-label">
                            <input type="checkbox" checked={newUser.send_invite}
                                onChange={(e) => setNewUser({ ...newUser, send_invite: e.target.checked })}
                                className="mt-0.5" data-testid="new-user-send-invite" />
                            <span className="text-[#52525b]">
                                <strong className="text-[#0d1628]">Enviar email de convite</strong> com a palavra-passe temporária ao novo utilizador (usa o SMTP da empresa configurado em cima). Se o SMTP estiver em branco, o utilizador é criado mas o email não é enviado.
                            </span>
                        </label>
                        <div className="flex gap-3">
                            <button type="submit" className="btn-primary" data-testid="create-user-submit">Criar</button>
                            <button type="button" className="btn-ghost" onClick={() => setShowInvite(false)}>Cancelar</button>
                        </div>
                    </form>
                )}

                <div className="card-flat mt-6 p-0 overflow-hidden" data-testid="users-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[#e4e4e7]">
                                <th className="text-left p-4 label-caps">Nome</th>
                                <th className="text-left p-4 label-caps">Email</th>
                                <th className="text-left p-4 label-caps">Papel</th>
                                <th className="text-right p-4 label-caps">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-b border-[#e4e4e7] last:border-0 hover:bg-[#f9f9fa]" data-testid={`user-row-${u.id}`}>
                                    <td className="p-4 text-[#0d1628]">{u.name}</td>
                                    <td className="p-4 text-[#52525b]">{u.email}</td>
                                    <td className="p-4">
                                        {u.id === user.id ? (
                                            <span className="label-caps">{ROLE_LABELS[u.role]}</span>
                                        ) : (
                                            <select
                                                className="input-clean bg-white py-1"
                                                value={u.role}
                                                onChange={(e) => updateRole(u.id, e.target.value)}
                                                data-testid={`role-select-${u.id}`}
                                            >
                                                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                            </select>
                                        )}
                                    </td>
                                    <td className="p-4 text-right">
                                        {u.id !== user.id && (
                                            <button onClick={() => deleteUser(u.id)} className="text-[#52525b] hover:text-red-600 p-2" data-testid={`delete-user-${u.id}`}>
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
